import React from "react";
import Md from "./Markdown.jsx";
import { classify, isSigningLine, gridColRole, isSectionRow, cellText, checkKey, checkOptions, fmtTime, fmtDate, BRAND, GOLD } from "./helpers.js";

/**
 * Renders one New Hire Packet form.
 * mode: "employee" | "hr". Fields owned by the other party are shown read-only
 * (HR-owned items are shaded gold so the new hire can see what the office completes).
 */
export default function FormRenderer({ form, data, mode, locked, images, stamps, employee, onChange, onStamp, busyKey }) {
  const edEmp = mode === "employee" && !locked;
  const edHr = mode === "hr" && !locked;
  const can = (role) => (role === "employee" && edEmp) || (role === "hr" && edHr);
  const canStamp = (key, role) => can(role) && stamps && stamps[key];

  const src = (st, kind) => (st && typeof st === "object" ? (images?.[st.by || "employee"] || {})[kind] : null);
  const Stamp = ({ st, kind }) => { const s = src(st, kind); return s ? <img src={s} alt={kind === "sig" ? "signature" : "initials"} className={kind === "sig" ? "h-10 max-w-[220px] object-contain" : "h-7 object-contain mx-auto"} /> : null; };
  const Meta = ({ st }) => <span className="block text-[10px] text-gray-500 leading-tight">Signed electronically {fmtTime(st.at)}{st.ip ? ` · IP ${st.ip}` : ""}</span>;
  const StampButton = ({ k, kind }) => (
    <button type="button" disabled={busyKey === k} onClick={() => onStamp(k)}
      className={`rounded-lg font-semibold text-white disabled:opacity-60 ${kind === "sig" ? "px-4 py-2 text-sm" : "px-3 py-1.5 text-xs"}`}
      style={{ backgroundColor: kind === "sig" ? BRAND : GOLD }}>
      {busyKey === k ? "…" : kind === "sig" ? "Click to sign" : "Initial"}
    </button>
  );
  // Plain function (not a component) so inputs keep their identity — and focus — across re-renders.
  const input = (k, label, role) => (
    <input
      key={k}
      defaultValue={data[k] || ""}
      onBlur={(e) => { if ((e.target.value || "") !== (data[k] || "")) onChange(k, e.target.value); }}
      placeholder={/Birth/.test(label) ? "MM/DD/YYYY" : ""}
      inputMode={/Social Security|Phone|ZIP/i.test(label) ? "numeric" : undefined}
      autoComplete={/Social Security/.test(label) ? "off" : undefined}
      className={`w-full border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 ${role === "hr" ? "bg-amber-50 border-amber-200" : "bg-white border-gray-300"}`}
      style={{ "--tw-ring-color": BRAND }}
    />
  );
  const hrBadge = (role) => role === "hr" && mode === "employee" ? <em className="not-italic text-[10px] text-amber-700 ml-1">· completed by HR</em> : null;
  const owned = (role) => (role === "hr" ? "bg-amber-50/70 border-amber-200" : "");

  const sigField = (lab, st, key, role, textKey) => {
    const k = classify(lab);
    let body;
    if (k === "sig") body = st ? <><Stamp st={st} kind="sig" /><Meta st={st} /></> : canStamp(key, role) ? <StampButton k={key} kind="sig" /> : <span className="block h-8" />;
    else if (k === "name") body = <span className="font-semibold">{st ? st.name : ""}</span>;
    else if (k === "date") body = <span className="font-semibold">{st ? fmtDate(st.at) : ""}</span>;
    else if (k === "initials") body = <span className="font-semibold">{st ? st.initials : ""}</span>;
    else if (k === "static") body = <span className="font-semibold">{lab}</span>;
    else if (textKey && can(role)) body = input(textKey, lab, role);
    else body = <span className="font-semibold">{(textKey && data[textKey]) || (st && st.title) || ""}</span>;
    return (
      <div key={lab + key} className={`${k === "sig" ? "sm:flex-[2]" : "sm:flex-1"} border-b border-gray-700 pb-1 min-h-[44px]`}>
        <label className="block text-[10px] uppercase tracking-wide text-gray-500">{lab}</label>
        {body}
      </div>
    );
  };

  return (
    <div className="space-y-2 text-[15px] leading-relaxed text-gray-800">
      {form.model.map((el, i) => {
        switch (el.t) {
          case "p": return <p key={i} className={el.small ? "text-sm" : ""}><Md text={el.text} /></p>;
          case "h": return <h3 key={i} className="text-lg font-bold mt-4" style={{ color: BRAND }}><Md text={el.text} /></h3>;
          case "bullet": return <ul key={i} className={`list-disc ml-6 ${el.small ? "text-sm" : ""}`}><li><Md text={el.text} /></li></ul>;
          case "note": return (
            <div key={i} className="border-l-4 bg-slate-50 px-3 py-2 text-sm rounded-r" style={{ borderColor: BRAND }}>
              {Array.isArray(el.text) ? el.text.map((x, n) => <p key={n} className="mb-1"><Md text={x} /></p>) : <Md text={el.text} />}
            </div>
          );
          case "line": {
            if (el.role === "prefill") return (
              <div key={i} className="grid sm:grid-cols-2 gap-3 my-2">
                <div className="border-b border-gray-400 pb-1"><label className="block text-[10px] uppercase tracking-wide text-gray-500">Employee Name</label><b>{employee?.name}</b></div>
                <div className="border-b border-gray-400 pb-1"><label className="block text-[10px] uppercase tracking-wide text-gray-500">Hire Date</label><b>{employee?.hire_date || ""}</b></div>
              </div>
            );
            if (isSigningLine(el)) {
              const key = `L${i}`;
              return <div key={i} className="flex flex-col sm:flex-row gap-3 my-3">{el.fields.map((lab) => sigField(lab, data[key], key, "employee", null))}</div>;
            }
            return (
              <div key={i} className={`grid gap-3 my-2 rounded ${owned(el.role)} ${el.role === "hr" ? "p-2 border" : ""}`} style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${el.fields.length > 2 ? 150 : 200}px, 1fr))` }}>
                {el.fields.map((lab, j) => {
                  const k = `L${i}_${j}`;
                  const kind = classify(lab);
                  return (
                    <div key={j}>
                      <label className="block text-[11px] uppercase tracking-wide text-gray-500">{lab || "(continued)"}{hrBadge(el.role)}</label>
                      {kind === "static" ? <b>{lab}</b> : can(el.role) ? input(k, lab, el.role) : <div className="border-b border-gray-400 min-h-[28px] font-semibold py-1">{data[k] || ""}</div>}
                    </div>
                  );
                })}
              </div>
            );
          }
          case "points": return (
            <div key={i} className={`divide-y border rounded-lg ${owned(el.role)}`}>
              {el.items.map((it, k) => {
                const key = `P${i}_${k}`;
                const st = data[key];
                return (
                  <div key={k} className="flex gap-3 p-3">
                    <div className="w-7 shrink-0 font-bold" style={{ color: GOLD }}>{it.n}</div>
                    <div className="flex-1 min-w-0">{it.text.map((x, n) => <p key={n} className="mb-1"><Md text={x} /></p>)}</div>
                    <div className="w-24 shrink-0 text-center self-end">
                      {st ? <><Stamp st={st} kind="init" /><span className="block text-[10px] text-gray-500 leading-tight">{fmtTime(st.at)}</span></>
                        : canStamp(key, el.role) ? <StampButton k={key} kind="init" /> : <span className="block h-7" />}
                      <small className="block text-[9px] tracking-wider text-gray-500 border-t border-gray-700 mt-1 pt-0.5">{el.role === "hr" ? "HR INITIALS" : "INITIALS"}</small>
                    </div>
                  </div>
                );
              })}
            </div>
          );
          case "check": {
            const key = checkKey(el, i);
            const opts = checkOptions(el);
            const editable = can(el.role);
            const radio = (value, label, n) => (
              <label key={n} className={`flex items-start gap-2 py-1 ${editable ? "cursor-pointer" : ""}`}>
                <input type="radio" className="mt-1.5 accent-[#154E93] disabled:opacity-100" name={key} disabled={!editable} checked={String(data[key] ?? "") === String(value)} onChange={() => onChange(key, String(value))} />
                <span><Md text={label} /></span>
              </label>
            );
            return (
              <div key={i} className={`rounded px-2 ${owned(el.role)} ${el.role === "hr" ? "border py-1" : ""}`}>
                {opts ? opts.map((o, n) => radio(n, o, n)) : radio(i, el.text, 0)}
                {el.role === "hr" && mode === "employee" && <em className="text-[11px] text-amber-700">Selected by HR</em>}
              </div>
            );
          }
          case "grid": return (
            <div key={i} className="overflow-x-auto -mx-1 px-1">
              <table className="w-full text-sm border-collapse min-w-[520px]">
                <thead><tr>{el.headers.map((h, n) => <th key={n} className="text-left text-white px-2 py-1.5 font-semibold" style={{ backgroundColor: "#1B3A6B" }}>{h}</th>)}</tr></thead>
                <tbody>
                  {el.rows.map((row, r) => {
                    if (isSectionRow(row)) return <tr key={r}><td colSpan={el.headers.length} className="bg-slate-100 font-bold px-2 py-1" style={{ color: "#1B3A6B" }}><Md text={row[0].t} /></td></tr>;
                    return (
                      <tr key={r}>
                        {row.map((cell, c) => {
                          const key = `G${i}_${r}_${c}`;
                          const cr = gridColRole(el, c);
                          if (c === 0 || cr === "readonly") return <td key={c} className="border px-2 py-1 align-top"><Md text={cellText(cell)} /></td>;
                          if (cr === "employee" && el.cols) {
                            const st = data[key];
                            return <td key={c} className="border px-2 py-1 text-center align-middle">{st ? <Stamp st={st} kind="init" /> : canStamp(key, "employee") ? <StampButton k={key} kind="init" /> : null}</td>;
                          }
                          const hrOwned = cr === "hr" || cr === "annual";
                          const editable = (hrOwned && edHr) || (cr === "employee" && edEmp);
                          const hint = cellText(cell);
                          return (
                            <td key={c} className={`border px-2 py-1 align-top ${hrOwned ? "bg-amber-50/70" : ""}`}>
                              {hint && <span className="block text-[11px] text-gray-500">{hint}</span>}
                              {editable ? input(key, el.headers[c], hrOwned ? "hr" : "employee") : <span className="font-semibold">{data[key] || ""}</span>}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
          case "sig": return (
            <div key={i} className="mt-4 space-y-3">
              {el.statement && <p className="font-semibold" style={{ color: "#1B3A6B" }}><Md text={el.statement} /></p>}
              {el.parties.map((party, p) => {
                const key = `S${i}_${p}`;
                const st = data[key];
                return (
                  <div key={p} className={`rounded-lg p-3 border ${party.role === "hr" ? "bg-amber-50/70 border-amber-200" : "bg-white"}`}>
                    <div className="text-[11px] font-bold tracking-widest uppercase" style={{ color: GOLD }}>
                      {party.who}{party.role === "hr" && mode === "employee" && <em className="not-italic normal-case tracking-normal text-amber-700 font-normal"> · signed by the Company</em>}
                    </div>
                    {party.lines.map((line, li) => (
                      <div key={li} className="flex flex-col sm:flex-row gap-3 mt-2">
                        {line.map((lab, fj) => sigField(lab, st, key, party.role, `${key}_${li}_${fj}`))}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          );
          default: return null;
        }
      })}
    </div>
  );
}
